import React from 'react';
import { Alert, Col, Collapse, Row, Statistic, Tag, Typography } from 'antd';
import { CreateMassiveResponse } from 'services/MassiveService';

const { Text } = Typography;

interface Props {
  result: CreateMassiveResponse;
  allowPartial: boolean;
}

const countErrors = (result: CreateMassiveResponse) =>
  (result.errors ?? []).reduce((acc, row) => acc + row.errors.length, 0);

const describeDefaults = (applied?: Record<string, number>) => {
  const entries = Object.entries(applied ?? {});
  if (!entries.length) return null;
  return entries
    .map(([field, count]) => `${field} → ${count} filas`)
    .join(' · ');
};

const MassiveUploadResult = ({ result, allowPartial }: Props) => {
  const created = !!result.massive_id;
  const defaults = describeDefaults(result.applied_defaults);

  return (
    <div>
      {created ? (
        <Alert
          type="success"
          showIcon
          message={`${result.accepted} paquetes encolados en la masiva ${result.massive_id}`}
          description="Se están creando en segundo plano; la grilla se actualiza sola."
          style={{ marginBottom: 16 }}
        />
      ) : (
        <Alert
          type="error"
          showIcon
          message="No se creó ningún paquete"
          description={
            allowPartial
              ? 'Ninguna fila del archivo pasó la validación. Corregilo y volvé a subirlo.'
              : `${result.rejected} de ${result.total} filas no pasaron la validación y “Crear con fallas” estaba desmarcado. Corregí el archivo y volvé a subirlo.`
          }
          style={{ marginBottom: 16 }}
        />
      )}

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Statistic title="Filas en el archivo" value={result.total} />
        </Col>
        <Col span={6}>
          <Statistic
            title={created ? 'Aceptadas' : 'Creadas'}
            value={result.accepted}
          />
        </Col>
        <Col span={6}>
          <Statistic title="Rechazadas" value={result.rejected} />
        </Col>
        <Col span={6}>
          <Statistic title="Errores en total" value={countErrors(result)} />
        </Col>
      </Row>

      {result.errors?.length ? (
        <Collapse
          size="small"
          items={result.errors.map((row) => ({
            key: String(row.row),
            label: (
              <span>
                <Text strong>Fila {row.row}</Text>{' '}
                <Text type="secondary">{row.tracking || '(autogenerado)'}</Text>{' '}
                <Tag>
                  {row.errors.length}{' '}
                  {row.errors.length === 1 ? 'error' : 'errores'}
                </Tag>
              </span>
            ),
            children: (
              <div>
                {row.errors.map((error) => (
                  <div key={`${error.column}-${error.code}`}>
                    <Tag color="error">{error.column}</Tag>
                    <Text type="secondary">
                      {error.value ? `“${error.value}”` : '(vacío)'}
                    </Text>{' '}
                    {error.message}
                  </div>
                ))}
              </div>
            ),
          }))}
        />
      ) : null}

      {created && result.rejected > 0 ? (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 16 }}
          message="El Excel de descarga trae sólo las filas rechazadas"
          description="Corregilas y volvé a subir ese mismo archivo: como las aceptadas no están ahí, no se duplica nada."
        />
      ) : null}

      {defaults ? (
        <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
          Valores por defecto aplicados: {defaults}. El resto traía su propio
          valor en el Excel.
        </Text>
      ) : null}
    </div>
  );
};

export default MassiveUploadResult;
